import { useCallback, useEffect, useId, useRef, useState } from "react";
import type { KeyboardEvent, ReactElement } from "react";
import { themeVar as colors } from "../design-tokens";
import { cadFieldHistory } from "./api";
import { historyValues, isHistoryResult, landAreaConflict, missingStages } from "./fieldHistory";

/**
 * "History" — a small, keyboard-accessible per-field affordance (owner spec 2026-10-04 §2): opens
 * every value `GET /api/cad/field-history` holds for one CAD field, newest first, each with its
 * stage/year/date. Read-only: opening it never touches `value`, `checked` or any other key on the
 * grid row it sits beside — it can only ever ADD a view, never change the field.
 *
 * RENDERS NOTHING when there is no CAD record to ask about (`county`/`propId` absent) — not a
 * disabled button. A control that is always inert is the dead-control defect the brief calls out
 * by name; the honest thing when there is genuinely nothing to show history FOR is to not offer
 * the control at all.
 *
 * RENDERS THE SERVER'S OWN SENTENCE, VERBATIM. `field_history.build_history` already composes
 * each value's full label ("Market value $412,000: Collin CAD, ... record 42, dataset v") through
 * `ingestion.core.provenance.value_label`; ADR-0002 (React is a pure renderer) means this panel
 * prints `value.label`, never reassembles the display/stage/date/record into a second sentence.
 *
 * Coded directly against the landed route (`src/server/field_history.py`,
 * `tests/test_field_history.py`), read on this worktree 2026-10-04 — not a re-briefed guess.
 */
export function FieldHistoryDisclosure({
  county,
  propId,
  field,
  fieldLabel,
}: {
  county: string | null | undefined;
  propId: string | null | undefined;
  field: string;
  /** The agent's own word for the field, e.g. "Living area (GLA)" — used only in the panel's
   * heading and its aria-label, never sent to the server (the wire key is `field`). */
  fieldLabel: string;
}): ReactElement | null {
  const [open, setOpen] = useState(false);
  const [result, setResult] = useState<ReturnType<typeof historyValues> | null>(null);
  const [missing, setMissing] = useState<ReturnType<typeof missingStages>>([]);
  const [conflict, setConflict] = useState<ReturnType<typeof landAreaConflict>>(null);
  const [error, setError] = useState<string | null>(null);
  const [notAvailable, setNotAvailable] = useState<string | null>(null);
  const panelId = useId();
  const buttonRef = useRef<HTMLButtonElement | null>(null);

  const load = useCallback(() => {
    if (!county || !propId) return undefined;
    setError(null);
    setNotAvailable(null);
    setResult(null);
    setMissing([]);
    setConflict(null);
    const controller = new AbortController();
    cadFieldHistory(county, propId, field, controller.signal)
      .then((payload) => {
        if (controller.signal.aborted) return;
        if (!isHistoryResult(payload)) {
          setError("That history came back in a shape this panel does not recognize.");
          return;
        }
        setResult(historyValues(payload));
        setMissing(missingStages(payload));
        setConflict(landAreaConflict(payload));
      })
      .catch((err: unknown) => {
        if (controller.signal.aborted) return;
        const status = (err as { status?: number } | null)?.status;
        const detail = (err as { body?: { detail?: { reason?: string } } } | null)?.body?.detail;
        const reason = detail?.reason;
        if (status === 404 || status === 400) {
          // Either the route refused this county/record/field by name (said plainly, via
          // `reason`), or — before LVS's route ships to a given deployment — it simply is not
          // there yet. Both are stated; neither spins forever.
          setNotAvailable(reason ?? "History isn't available for this field yet.");
          return;
        }
        setError(err instanceof Error ? err.message : "That history did not load.");
      });
    return () => controller.abort();
  }, [county, propId, field]);

  useEffect(() => {
    if (!open) return undefined;
    return load();
  }, [open, load]);

  if (!county || !propId) return null;

  function toggle() {
    setOpen((o) => !o);
  }

  function onKeyDown(e: KeyboardEvent<HTMLDivElement>) {
    if (e.key === "Escape" && open) {
      e.stopPropagation();
      setOpen(false);
      buttonRef.current?.focus();
    }
  }

  return (
    <span style={{ position: "relative", display: "inline-block" }} onKeyDown={onKeyDown}>
      <button
        type="button"
        ref={buttonRef}
        aria-expanded={open}
        aria-controls={open ? panelId : undefined}
        aria-label={`History for ${fieldLabel}`}
        onClick={toggle}
        style={{
          marginLeft: 6,
          fontSize: 11,
          color: colors.accent,
          background: "none",
          border: "none",
          padding: 0,
          cursor: "pointer",
          textDecoration: "underline",
        }}
      >
        History
      </button>
      {open && (
        <div
          id={panelId}
          role="region"
          aria-label={`${fieldLabel} — field history`}
          style={{
            position: "absolute",
            right: 0,
            top: "100%",
            zIndex: 20,
            marginTop: 4,
            minWidth: 260,
            maxWidth: 380,
            background: colors.bgSurface,
            border: `1px solid ${colors.border}`,
            borderRadius: 8,
            padding: 10,
            boxShadow: "0 4px 16px rgba(0,0,0,0.15)",
          }}
        >
          <p style={{ margin: "0 0 6px", fontSize: 12, fontWeight: 600, color: colors.textPrimary }}>
            {fieldLabel} — earlier values
          </p>
          {notAvailable && (
            <p style={{ margin: 0, fontSize: 12, color: colors.textSecondary }}>{notAvailable}</p>
          )}
          {error && (
            <p role="alert" style={{ margin: 0, fontSize: 12, color: colors.caution }}>
              {error}
            </p>
          )}
          {!notAvailable && !error && result === null && (
            <p style={{ margin: 0, fontSize: 12, color: colors.textSecondary }}>Reading the history…</p>
          )}
          {/* LOT SIZE AND ACREAGE DISAGREE — both figures, each with its own source, under one calm
              note. No single number is shown and nothing is picked (server: field_history.py). */}
          {conflict && (
            <div>
              <p style={{ margin: "0 0 4px", fontSize: 12, color: colors.textPrimary }}>{conflict.note}</p>
              <ul style={{ listStyle: "none", margin: 0, padding: 0, display: "flex", flexDirection: "column", gap: 4 }}>
                {conflict.figures.map((f) => (
                  <li key={f.field} style={{ fontSize: 12, color: colors.textPrimary, lineHeight: 1.4 }}>
                    {f.label}
                  </li>
                ))}
              </ul>
            </div>
          )}
          {!conflict && result !== null && result.length === 0 && missing.length === 0 && !notAvailable && !error && (
            <p style={{ margin: 0, fontSize: 12, color: colors.textSecondary }}>
              Nothing else is on record for this field.
            </p>
          )}
          {result !== null && result.length > 0 && (
            <ul style={{ listStyle: "none", margin: 0, padding: 0, display: "flex", flexDirection: "column", gap: 6 }}>
              {result.map((v, i) => (
                <li key={`${v.stage}-${v.year}-${i}`} style={{ fontSize: 12, color: colors.textPrimary, lineHeight: 1.4 }}>
                  {/* RENDERED VERBATIM — ADR-0002. The server already wrote this sentence. */}
                  {v.label}
                  {v.current && (
                    <span style={{ marginLeft: 6, color: colors.verified, fontWeight: 600 }}>· current</span>
                  )}
                </li>
              ))}
            </ul>
          )}
          {/* A STAGE THIS LOAD NEVER SHIPPED — the calm note, never styled as an error (a dataset
              genuinely not carrying a supplement roll yet is not a defect). */}
          {missing.length > 0 && (
            <ul style={{ listStyle: "none", margin: "6px 0 0", padding: 0, display: "flex", flexDirection: "column", gap: 4 }}>
              {missing.map((m, i) => (
                <li key={`${m.stage}-${i}`} style={{ fontSize: 12, color: colors.textSecondary, fontStyle: "italic" }}>
                  {m.stage ? `${capitalize(m.stage)}: ` : ""}
                  {m.reason}
                </li>
              ))}
            </ul>
          )}
        </div>
      )}
    </span>
  );
}

function capitalize(s: string): string {
  return s.length ? s.charAt(0).toUpperCase() + s.slice(1) : s;
}
